import {Component, Injector, OnInit, ViewChild} from '@angular/core';
import {Category} from "../../models/category";
import {CategoryService} from "../../services/category/category.service";
import {CompanyService} from "../../services/company/company.service";
import {ActivatedRoute, Router} from "@angular/router";
import {AthleteService} from "../../services/athlete/athlete.service";
import {PaymentService, SiblingDiscount} from "../../services/payment/payment.service";
import {Parent} from "../../models/parent";
import {BaseComponent} from "../base/base.component";
import Swal from "sweetalert2";
import {User} from "../../models/user";
import {environment} from "../../environments/environment";
import {Company} from "../../models/company";
import {UserService} from "../../services/user/user.service";
import {Season} from "../../models/season";
import {SeasonService} from "../../services/season/season.service";

@Component({
    selector: 'app-payment',
    templateUrl: './payment.component.html',
    styleUrls: ['./payment.component.scss']
})
export class PaymentComponent extends BaseComponent implements OnInit {

    @ViewChild('nexiForm') nexiForm?: any;
    @ViewChild('nexiRatesForm') nexiRatesForm?: any;

    categories: Category[] = [];
    allCategories: Category[] = [];
    currentSeason?: Season
    categorySelected: string = '';
    company: Company = new Company();
    companyId: string = localStorage.getItem('wisper_companyId') ?? '';
    athleteId: string = '';
    nexiEnabled: boolean = false;
    nexiRatesEnabled: boolean = false;
    //soisyEnabled: boolean = false;
    wireTransferEnabled: boolean = false;
    localPaymentEnabled: boolean = false;
    appPagoRatesEnabled: boolean = false;
    posNexiEnabled: boolean = false;
    applyReducedPrice: boolean = false;
    siblingDiscount: SiblingDiscount | null = null;
    loading: boolean = true;
    reviewing: boolean = false;
    athleteName: string = '';

    athleteParents: Parent[] = [];
    invoiceParents: Parent[] | User[] = [];

    // payment
    nexiAlias: string | undefined;
    nexiRatesAlias: string | undefined;
    importo: number = 0;
    divisa: string = 'EUR';
    codTrans: string = '';
    url: string = '/thankyou';
    url_back: string = '/users/dashboard';
    urlpost: string = '';
    value3dsDinamico: string = 'SCA';
    nexiMac: string = '';
    nexiRatesMac: string = '';
    email: string = localStorage.getItem('wisper_email') ?? '';

    paymentMethod: string = '';
    termsAndConditions: boolean = false;

    user?: User
    userType?: string

    constructor(
        public router: ActivatedRoute,
        public paymentService: PaymentService,
        public categoryService: CategoryService,
        public companyService: CompanyService,
        public athleteService: AthleteService,
        public userService: UserService,
        public serviceInjector: Injector,
        public routerNavigator: Router,
        public seasonService: SeasonService
    ) {
        super(serviceInjector);
    }

    async ngOnInit() {
        const userId = localStorage.getItem('wisper_userId') ?? '';
        this.athleteId = this.router.snapshot.params['athleteId'] ?? '';

        try {
            const [user, seasons, companyResponse, parents, athlete] = await Promise.all([
                this.userService.getUserById(userId),
                this.seasonService.getSeasons(this.companyId, {isCurrent: true}),
                this.companyService.getCompanyById(this.companyId),
                this.athleteService.getAthleteParents(this.athleteId),
                this.athleteService.getAthleteById(this.athleteId),
            ]);
            this.athleteName = athlete ? `${athlete.firstName} ${athlete.lastName}`.trim() : '';
            this.user = user;
            this.userType = user?.subtype;
            this.currentSeason = seasons?.data?.[0];
            this.athleteParents = parents ?? [];

            this.company = companyResponse.data;
            this.nexiAlias = this.company.nexiApiKey ?? '';
            this.nexiRatesAlias = this.company.nexiRatesApiKey ?? '';
            this.nexiEnabled = !!this.company.nexiEnabled;
            this.wireTransferEnabled = !!this.company.wireTransferEnabled;
            this.localPaymentEnabled = !!this.company.localPaymentEnabled;
            this.nexiRatesEnabled = !!this.company.nexiRatesEnabled;
            this.appPagoRatesEnabled = !!this.company.appPagoRatesEnabled;
            this.posNexiEnabled = !!this.company.posNexiEnabled;

            const [allCategories] = await Promise.all([
                this.categoryService.getCategories(this.companyId, false, {seasonId: this.currentSeason?.id}),
                this.loadSiblingDiscount(),
            ]);
            this.allCategories = allCategories;
            this.categories = this.allCategories
                .filter((category: Category) => category.parentId !== null)
                .map((category: Category) => ({...category, name: `${category.parent!.name} - ${category.name}`}));
        } catch (e) {
            console.error('error loading payment page: ', e);
        } finally {
            this.loading = false;
        }
    }

    /**
     * Lo sconto fratelli è calcolato dal backend sulla data della richiesta di iscrizione per la stagione:
     * se un fratello ha richiesto l'iscrizione prima (anche senza aver ancora pagato) si applica il prezzo ridotto.
     * Il backend ricalcola comunque l'importo alla creazione del pagamento.
     */
    async loadSiblingDiscount() {
        if (!this.currentSeason?.id || !this.athleteId) {
            this.siblingDiscount = null;
            this.applyReducedPrice = false;
            return;
        }
        this.siblingDiscount = await this.paymentService.getSiblingDiscount(this.athleteId, this.currentSeason.id);
        this.applyReducedPrice = !!this.siblingDiscount?.eligible;
    }

    get selectedCategory(): Category | undefined {
        return this.categories.find((category: Category) => category.id === this.categorySelected);
    }

    /** Prezzo da pagare per la categoria (ridotto solo se idoneo e configurato). */
    priceFor(category: Category | undefined): number {
        if (!category) return 0;
        const fullPrice = Number(category.fullPrice);
        const reducedPrice = Number(category.reducedPrice);
        if (!this.applyReducedPrice || !Number.isFinite(reducedPrice) || reducedPrice < 0) return fullPrice;
        return Math.min(reducedPrice, fullPrice);
    }

    hasDiscount(category: Category | undefined): boolean {
        return !!category && this.priceFor(category) < Number(category.fullPrice);
    }

    getCategoryAmount() {
        return this.priceFor(this.selectedCategory);
    }

    /** Passa dal form al riepilogo (nessuna chiamata al backend). */
    goToReview() {
        if (this.utils.isEmpty(this.invoiceParents) && this.athleteParents.length > 0) {
            this.showErrorMessage('Seleziona almeno un genitore');
            return;
        }
        this.reviewing = true;
        window.scrollTo({top: 0, behavior: 'smooth'});
    }

    editPayment() {
        this.reviewing = false;
        window.scrollTo({top: 0, behavior: 'smooth'});
    }

    paymentMethodLabel(method: string = this.paymentMethod): string {
        switch (method) {
            case 'transfer': return 'Bonifico bancario';
            case 'nexi': return 'Carta di credito o debito (Nexi)';
            case 'nexiRates': return 'Rate mensili PagoDIL';
            case 'appPagoRates': return 'Rate AppPago';
            case 'localPayment':
                if (this.localPaymentEnabled && this.posNexiEnabled) return 'In segreteria (contanti o POS)';
                return this.posNexiEnabled ? 'In segreteria (solo POS)' : 'In segreteria (solo contanti)';
            default: return '';
        }
    }

    /** Commissione percentuale del metodo a rate scelto (0 se non prevista). */
    get commissionPercent(): number {
        const percent = this.paymentMethod === 'nexiRates' ? this.company.nexiRatesCommissionPercent
            : this.paymentMethod === 'appPagoRates' ? this.company.appPagoCommissionPercent : 0;
        return Number(percent) || 0;
    }

    /** Totale stimato: quota + eventuale commissione del pagamento a rate. */
    get totalAmount(): number {
        const amount = this.getCategoryAmount();
        return Math.round(amount * (1 + this.commissionPercent / 100) * 100) / 100;
    }

    get invoiceRecipients(): string {
        return (this.invoiceParents as Array<Parent | User>).map(parent => `${parent.firstName} ${parent.lastName}`).join(', ');
    }

    formatAmount(value: number): string {
        return value.toLocaleString('it-IT', {style: 'currency', currency: 'EUR'});
    }

    async createPaymentRequest() {
//retrieve parameter
        if (this.utils.isEmpty(this.invoiceParents) && this.athleteParents.length > 0) {
            this.showErrorMessage('Seleziona almeno un genitore');
            return;
        }
        const result = await Swal.fire({
            icon: 'question',
            title: 'Confermi il pagamento?',
            text: `${this.formatAmount(this.totalAmount)} · ${this.paymentMethodLabel()}`,
            width: 380,
            showCancelButton: true,
            reverseButtons: true,
            confirmButtonText: 'Sì, conferma',
            cancelButtonText: 'Annulla'
        });

        if (result.isConfirmed) {
            this.showSpinner();
            let response: any = await this.paymentService.paymentRequest(this.athleteId, this.paymentMethod, this.getCategoryAmount(), this.categorySelected, this.invoiceParents.map((elem: Parent | User) => elem.id!), 'http://localhost:4200/thankyou', 'http://localhost:4200/error');
            this.hideSpinner();
            console.log('response: ', response)
            if (response?.code === 'ATHLETE_SEASON_ALREADY_PAID') {
                await Swal.fire({
                    icon: 'info',
                    title: 'Iscrizione già pagata',
                    text: 'Abbiamo già ricevuto il pagamento per questa categoria: non serve pagare di nuovo.'
                });
                this.routerNavigator.navigate(['/users/dashboard']);
                return;
            }
            if (!response || !response.success) {
                await Swal.fire({
                    icon: 'error',
                    title: "Errore nella creazione del pagamento"
                });
                return;
            }

            // Nexi
            if (this.paymentMethod === 'nexi') {
                console.log('res: ', response.data)

                this.nexiAlias = response.data.alias;
                this.url = environment.baseUrl + '/thankyou';
                this.url_back = environment.baseUrl + '/users/dashboard';

                this.importo = response.data.centsAmount;
                this.codTrans = response.data.transactionId;
                this.nexiMac = response.data.mac;
                this.urlpost = response.data.urlpost;

                setTimeout(() => this.nexiForm!.nativeElement.submit(), 100);
            }

            if (this.paymentMethod === 'nexiRates') {
                console.log('res: ', response.data)

                this.nexiRatesAlias = response.data.alias;
                this.url = environment.baseUrl + '/thankyou';
                this.url_back = environment.baseUrl + '/users/dashboard';

                this.importo = response.data.centsAmount;
                this.codTrans = response.data.transactionId;
                this.nexiRatesMac = response.data.mac;
                this.urlpost = response.data.urlpost;

                setTimeout(() => this.nexiRatesForm!.nativeElement.submit(), 100);
            }

            // Soisy
            /*if (this.paymentMethod === 'soisy') {
                const { url } = response.data;
                if ( !url || url === '' ) {
                    Swal.fire({icon: 'error', title: 'Errore nel redirect verso Soisy'});
                } else {
                    window.location.href = url;
                }
            }*/

            //Bonifico
            if (this.paymentMethod === 'transfer') {
                let response: any = await this.companyService.getCompanyById(localStorage.getItem('wisper_companyId') ?? '');

                if (!response.success) {
                    Swal.fire({icon: 'error', title: 'Errore recupero dati bonifico'})
                    return;
                }
                this.routerNavigator.navigate(['/iban-details'], {queryParams: {ibanSelected: true}});
            }

            if (this.paymentMethod === 'localPayment') {
                let response: any = await this.companyService.getCompanyById(localStorage.getItem('wisper_companyId') ?? '');

                if (!response.success) {
                    Swal.fire({icon: 'error', title: 'Errore recupero dati bonifico'})
                    return;
                }
                this.routerNavigator.navigate(['/iban-details']).then();
            }

            //Banca sella rates
            if (this.paymentMethod === 'appPagoRates') {
                window.location.href = response.data.url;
            }
        }
    }

    selectParent(selectedParent: Parent) {
        // @ts-ignore
        if (this.invoiceParents.find((invoiceParent: Parent) => invoiceParent.id === selectedParent.id)) {
            // @ts-ignore
            this.invoiceParents = this.invoiceParents.filter((parent: Parent) => parent.id !== selectedParent.id);
        } else {
            // @ts-ignore
            this.invoiceParents.push(selectedParent);
        }
    }

    getNexiUrl() {
        return environment.nexiUrl;
    }

    getValidMail(email: string) {
        return email.replaceAll("+1", "");
    }

    disablePayBtn() {
        return this.athleteParents.length > 0 && this.invoiceParents.length === 0;
    }

    // Parent validation
    parentComplete() {
        for (const parent of this.athleteParents) {
            if (
                parent!.firstName.trim() === '' ||
                parent!.lastName.trim() === '' ||
                !parent.fiscalCode ||
                parent.fiscalCode.trim() === '' ||
                !parent.bornDate ||
                parent.bornDate.trim() === '' ||
                !parent.bornCity ||
                parent.bornCity.trim() === '' ||
                parent!.address?.trim() === '' ||
                parent!.city?.trim() === '' ||
                parent!.zip?.trim() === ''
            ) {
                return false;
            }
        }
        return true;
    }
}
